// Tiny pub/sub store shared by the page (which publishes signals) and the
// globally mounted <LeadBot /> (which decides when to speak).
// No external deps, works on React 17 and 18.
import { useEffect, useState } from 'react';

const SESSION_KEY = 'rc_leadbot_session';
const SNOOZE_KEY = 'rc_leadbot_snooze_until';

const readSession = () => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; } catch { return {}; }
};

const session = readSession();

const INITIAL_PAGE_SIGNALS = {
    page: null,                 // 'search' while SearchForm is mounted
    search: null,               // contactSearchData of the current results
    searchKey: '',
    resultsLoading: false,
    resultsCount: 0,
    noRoutes: false,
    allWaitlisted: false,
    tatkalWindow: false,
    dateFlips: 0,
    deepScrollNoBook: false,
    returnedAfterAway: false,
    suppressed: {},             // reason -> bool (panel open, modal open, ...)
};

let state = {
    ...INITIAL_PAGE_SIGNALS,
    leadSubmitted: !!session.leadSubmitted,
    nudgesShown: session.nudgesShown || 0,
    nudgedKeys: session.nudgedKeys || [],
};

const listeners = new Set();

const persist = () => {
    try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
            leadSubmitted: state.leadSubmitted,
            nudgesShown: state.nudgesShown,
            nudgedKeys: state.nudgedKeys.slice(-20),
        }));
    } catch { /* storage blocked */ }
};

const emit = () => listeners.forEach(l => l(state));

export const getLeadBotState = () => state;

export const setLeadSignals = (partial) => {
    const changed = Object.keys(partial).some(k => state[k] !== partial[k]);
    if (!changed) return;
    state = { ...state, ...partial };
    emit();
};

export const setLeadBotSuppressed = (reasons) => {
    const next = { ...state.suppressed, ...reasons };
    const same = Object.keys(next).every(k => !!next[k] === !!state.suppressed[k]);
    if (same) return;
    state = { ...state, suppressed: next };
    emit();
};

export const resetPageSignals = () => {
    state = { ...state, ...INITIAL_PAGE_SIGNALS };
    emit();
};

export const bumpDateFlips = () => setLeadSignals({ dateFlips: state.dateFlips + 1 });

export const markLeadSubmitted = () => {
    state = { ...state, leadSubmitted: true };
    persist();
    emit();
};

export const recordNudge = (key) => {
    state = {
        ...state,
        nudgesShown: state.nudgesShown + 1,
        nudgedKeys: [...state.nudgedKeys, key],
    };
    persist();
    emit();
};

export const snoozeLeadBot = (hours = 24) => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + hours * 3600 * 1000)); } catch { /* ignore */ }
    emit();
};

export const isLeadBotSnoozed = () => {
    try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now(); } catch { return false; }
};

export const useLeadBotState = () => {
    const [s, setS] = useState(state);
    useEffect(() => {
        listeners.add(setS);
        setS(state);
        return () => { listeners.delete(setS); };
    }, []);
    return s;
};
